/** Assistant de paramétrage LyrraOne — première visite créateur. */

export type LyrraOneSetupWizardStep = 'template' | 'questions' | 'recap' | 'done';

export type LyrraOneSetupQuestionId = 'audience' | 'experience' | 'role' | 'teamSize' | 'industry';

export type LyrraOneSetupAnswers = Partial<Record<LyrraOneSetupQuestionId, string>>;

export interface LyrraOneSetupProfile extends LyrraOneSetupAnswers {
  templateId?: string;
  completedAt?: string;
}

export interface LyrraOneSetupQuestionOption {
  id: string;
  label: string;
}

export interface LyrraOneSetupQuestion {
  id: LyrraOneSetupQuestionId;
  title: string;
  subtitle?: string;
  options: LyrraOneSetupQuestionOption[];
}

export const LYRRAONE_SETUP_TEMPLATE_CATEGORIES = [
  'Tous',
  'Coaching',
  'Éducation',
  'Sport & fitness',
  'Cuisine',
  'Bien-être',
  'Business',
  'Créatif',
  'Art & design',
  'Santé',
  'Minimaliste',
] as const;

export const LYRRAONE_SETUP_QUESTIONS: LyrraOneSetupQuestion[] = [
  {
    id: 'audience',
    title: 'À qui vendez-vous principalement ?',
    subtitle: 'Cela nous aide à personnaliser votre espace.',
    options: [
      { id: 'individuals', label: 'Des particuliers' },
      { id: 'businesses', label: 'Des entreprises' },
      { id: 'both', label: 'Particuliers et entreprises' },
      { id: 'institutions', label: 'Des établissements / institutions' },
    ],
  },
  {
    id: 'experience',
    title: 'Quel est votre niveau avec la formation en ligne ?',
    options: [
      { id: 'beginner', label: 'Je débute avec la formation en ligne' },
      { id: 'some', label: "J'ai déjà publié quelques contenus" },
      { id: 'experienced', label: 'Je suis expérimenté(e)' },
      { id: 'expert', label: "C'est mon activité principale" },
    ],
  },
  {
    id: 'role',
    title: 'Quel titre vous décrit le mieux ?',
    options: [
      { id: 'coach', label: 'Coach' },
      { id: 'trainer', label: 'Formateur / Formatrice' },
      { id: 'consultant', label: 'Consultant / Consultante' },
      { id: 'creator', label: 'Créateur de contenu' },
      { id: 'other', label: 'Autre' },
    ],
  },
  {
    id: 'teamSize',
    title: 'Combien êtes-vous dans votre équipe ?',
    options: [
      { id: 'solo', label: 'Seul(e)' },
      { id: '2-4', label: '2 à 4 personnes' },
      { id: '5-10', label: '5 à 10 personnes' },
      { id: '10+', label: 'Plus de 10 personnes' },
    ],
  },
  {
    id: 'industry',
    title: 'Votre activité est surtout liée à…',
    options: [
      { id: 'creative', label: 'Arts créatifs & médias' },
      { id: 'business', label: 'Business & entrepreneuriat' },
      { id: 'health', label: 'Santé & bien-être' },
      { id: 'education', label: 'Éducation & pédagogie' },
      { id: 'tech', label: 'Tech & digital' },
      { id: 'other', label: 'Autre secteur' },
    ],
  },
];

export function getSetupQuestionLabel(questionId: LyrraOneSetupQuestionId, optionId: string): string {
  const question = LYRRAONE_SETUP_QUESTIONS.find((q) => q.id === questionId);
  return question?.options.find((o) => o.id === optionId)?.label ?? optionId;
}

export function isLyrraOneSetupWizardCompleted(preferences: unknown): boolean {
  if (!preferences || typeof preferences !== 'object' || Array.isArray(preferences)) return false;
  return (preferences as Record<string, unknown>).lyrraoneSetupWizardCompleted === true;
}

export function readLyrraOneSetupProfile(preferences: unknown): LyrraOneSetupProfile | null {
  if (!preferences || typeof preferences !== 'object' || Array.isArray(preferences)) return null;
  const profile = (preferences as Record<string, unknown>).lyrraoneSetupProfile;
  if (!profile || typeof profile !== 'object' || Array.isArray(profile)) return null;
  return profile as LyrraOneSetupProfile;
}

/** Signal global : le wizard bloque la visite guidée tant qu'il est ouvert. */
let setupWizardOpen = false;

export function setLyrraOneSetupWizardOpen(open: boolean): void {
  setupWizardOpen = open;
}

export function isLyrraOneSetupWizardOpen(): boolean {
  return setupWizardOpen;
}
